import React, { useState, useEffect } from 'react';
import { isPresent } from 'utils/helperMethods';
import { values, equals, isNil, assocPath, pluck } from 'ramda';
import Seo from 'sharedComponents/Seo';
import { useTranslation } from 'react-i18next';

import useApp from 'reducers/hooks/useApp';
import useRealEstateAgents from 'reducers/hooks/loanOfficer/useRealEstateAgents';

import { Typography } from '@material-ui/core';
import Search from './components/Search';
import Tabs from './components/Tabs';
import RealEstateAgentsList from './components/RealEstateAgentsList';

import useStyles from './useStyles';

const PER_PAGE = 20;

const TABS_INDEX_MAPPER = {
  all: 0,
  waitlist: 1,
  favorite: 2,
};

const LoanOfficerRealEstateAgents = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { currentUser } = useApp();

  const {
    realEstateAgents,
    meta,
    totalCount,
    loadRealEstateAgents,
    isRealEstateAgentsLoading,
    isRealEstateAgentsIdle,
    loadMoreRealEstateAgents,

    addToWaitlist,
    isAddingToWaitlist,

    loadWaitlistRealEstateAgents,
    waitlistRealEstateAgents,
    waitlistTotalCount,
    loadMoreWaitlistRealEstateAgents,
    waitlistRealEstateAgentsMeta,
    isWaitlistRealEstateAgentsLoading,
    isWaitlistRealEstateAgentsIdle,

    loadInFavoritesRealEstateAgents,
    loadMoreInFavoritesRealEstateAgents,
    inFavoritesRealEstateAgents,
    inFavoritesRealEstateAgentsMeta,
    isInFavoritesRealEstateAgentsLoading,
    isInFavoritesRealEstateAgentsIdle,
    inFavoritesTotalCount,
  } = useRealEstateAgents();

  const [activeTabIndex, setActiveTabIndex] = useState(TABS_INDEX_MAPPER.all);
  const [searchInput, setSearchInput] = useState('');

  const buildSearchParams = (input = null) => {
    const searchInputValue = isNil(input) ? searchInput : input;

    return isPresent(searchInputValue)
      ? { perPage: PER_PAGE, q: { searchFieldCont: searchInputValue } }
      : { perPage: PER_PAGE };
  };

  const buildWaitlistSearchParams = (input = null) => {
    const searchParams = buildSearchParams(input);
    return assocPath(['q', 'inRealEstateAgentWaitlistQ'], currentUser.id, searchParams);
  };

  const buildInFavoritesSearchParams = (input = null) => {
    const searchParams = buildSearchParams(input);
    return assocPath(['q', 'inRealEstateAgentFavoritesQ'], currentUser.id, searchParams);
  };

  const handleLoadRealEstateAgents = params => {
    loadRealEstateAgents(params);
  };

  const handleAddToWaitlist = realEstateAgent => () => {
    addToWaitlist({ id: realEstateAgent.id });
  };

  useEffect(() => {
    const searchParams = buildSearchParams();
    handleLoadRealEstateAgents(searchParams);

    const waitlistSearchParams = buildWaitlistSearchParams();
    loadWaitlistRealEstateAgents(waitlistSearchParams);

    const inFavoritesSearchParams = buildInFavoritesSearchParams();
    loadInFavoritesRealEstateAgents(inFavoritesSearchParams);
  }, []);

  const handleLoadMoreRealEstateAgents = params => {
    const searchParams = buildSearchParams();
    loadMoreRealEstateAgents({ ...searchParams, ...params });
  };

  const handleLoadMoreWaitlistRealEstatAgents = params => {
    const searchParams = buildWaitlistSearchParams();

    loadMoreWaitlistRealEstateAgents({ ...searchParams, ...params });
  };

  const handleLoadMoreInFavoritesRealEstatAgents = params => {
    const searchParams = buildInFavoritesSearchParams();

    loadMoreInFavoritesRealEstateAgents({ ...searchParams, ...params });
  };

  const isAllRealEstateAgentsTab = equals(activeTabIndex, TABS_INDEX_MAPPER.all);
  const isWaitlistTab = equals(activeTabIndex, TABS_INDEX_MAPPER.waitlist);
  const isFavoritesTab = equals(activeTabIndex, TABS_INDEX_MAPPER.favorite);

  const inWaitlistRealEstateAgentsIds = pluck('id')(waitlistRealEstateAgents);
  const inFavoritesRealEstateAgentsIds = pluck('id')(inFavoritesRealEstateAgents);

  const handleTabChange = (_event, newValue) => {
    setActiveTabIndex(newValue);
  };

  const handleSearchInputChange = value => {
    setSearchInput(value);

    switch (true) {
      case isAllRealEstateAgentsTab:
        const searchParams = buildSearchParams(value);
        handleLoadRealEstateAgents(searchParams);
        break;
      case isWaitlistTab:
        const waitlistSearchParams = buildWaitlistSearchParams(value);
        loadWaitlistRealEstateAgents(waitlistSearchParams);
        break;
      case isFavoritesTab:
        const inFavoritesSearchParams = buildInFavoritesSearchParams(value);
        loadInFavoritesRealEstateAgents(inFavoritesSearchParams);
        break;
      default:
        break;
    }
  };

  return (
    <div className={classes.container}>
      <Seo
        title={t('meta.LoanOfficerRealEstateAgent.title')}
        description={t('meta.LoanOfficerRealEstateAgent.description')}
      />
      <div className={classes.content}>
        <Typography variant="h1">{t('components.LoanOfficerRealEstateAgents.header')}</Typography>
        <Search onChange={handleSearchInputChange} />
        <Tabs
          currentTabIndex={activeTabIndex}
          tabIndexValues={values(TABS_INDEX_MAPPER)}
          onChange={handleTabChange}
          allTabsBadgeCount={totalCount}
          waitlistTabsBadgeCount={waitlistTotalCount}
          favoriteTabsBadgeCount={inFavoritesTotalCount}
        />
        {isAllRealEstateAgentsTab && (
          <RealEstateAgentsList
            realEstateAgents={realEstateAgents}
            meta={meta}
            isLoading={isRealEstateAgentsLoading || isRealEstateAgentsIdle}
            onLoadMore={handleLoadMoreRealEstateAgents}
            onAddToWaitlist={handleAddToWaitlist}
            inWaitlistRealEstateAgentsIds={inWaitlistRealEstateAgentsIds}
            inFavoritesRealEstateAgentsIds={inFavoritesRealEstateAgentsIds}
            isAddingToWaitlist={isAddingToWaitlist}
            showActions
          />
        )}
        {isWaitlistTab && (
          <RealEstateAgentsList
            realEstateAgents={waitlistRealEstateAgents}
            meta={waitlistRealEstateAgentsMeta}
            isLoading={isWaitlistRealEstateAgentsLoading || isWaitlistRealEstateAgentsIdle}
            onLoadMore={handleLoadMoreWaitlistRealEstatAgents}
          />
        )}
        {isFavoritesTab && (
          <RealEstateAgentsList
            realEstateAgents={inFavoritesRealEstateAgents}
            meta={inFavoritesRealEstateAgentsMeta}
            isLoading={isInFavoritesRealEstateAgentsLoading || isInFavoritesRealEstateAgentsIdle}
            onLoadMore={handleLoadMoreInFavoritesRealEstatAgents}
          />
        )}
      </div>
    </div>
  );
};

export default LoanOfficerRealEstateAgents;



// WEBPACK FOOTER //
// ./src/components/LoanOfficerRealEstateAgents/LoanOfficerRealEstateAgents.js